import { useCallback, useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import MediaAPI from "../../../api/MediaAPI";
import SmartlistAPI from "../../../api/SmartlistAPI";
import { getElementByUuid } from "../../../utils/data/Content";
import ItemStatus from "../../../utils/ItemStatus";
import matchesItemSearch from "../selectBoxItemSearch";
import { renumberRanks, sortByRank } from "../selectBoxRanks";

import type { Dispatch, SetStateAction } from "react";

import type { AppState } from "../../../reducers";
import type {
  SelectBoxDisplayItem,
  SelectBoxListFlags,
  SelectBoxSeedItem,
} from "../selectBox.types";

export interface UseSelectedItemsParams extends SelectBoxListFlags {
  items: SelectBoxSeedItem[];
  hasRank?: boolean;
  onNormalizeRanks?: (items: SelectBoxDisplayItem[]) => void;
}

export interface UseSelectedItemsResult {
  selectedItems: SelectBoxDisplayItem[];
  updateSelectedItems: (list: SelectBoxDisplayItem[]) => void;
  selectedStatus: string;
  setSelectedStatus: Dispatch<SetStateAction<string>>;
  selectedSearch: string;
  setSelectedSearch: Dispatch<SetStateAction<string>>;
  selectedFilteredItems: SelectBoxDisplayItem[];
}

export function useSelectedItems({
  items,
  hasRank,
  onNormalizeRanks,
  hasMediaList,
  hasCastingList,
  hasSmartlistList,
  hasDisplayList,
}: UseSelectedItemsParams): UseSelectedItemsResult {
  const { projectSlug } = useParams<{ projectSlug: string }>();

  const getCastingsQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getCastings,
    params: [{ perPage: 100000 }],
  });

  const { data: castingsData, isFetched: isFetchedCastings } = useQuery({
    ...getCastingsQuery,
    enabled: hasCastingList,
  });

  const getDisplayListQuery = SmartlistAPI.generateQuery({
    fn: SmartlistAPI.getDisplayList,
    params: [
      {
        projectSlug,
      },
    ],
  });

  const { data: displaysData, isFetched: isFetchedDisplays } = useQuery({
    ...getDisplayListQuery,
    enabled: hasDisplayList,
  });

  const contents = useSelector(
    (state: AppState) => state.fetchReducer.contents,
  );
  const smartlists = useSelector(
    (state: AppState) => state.fetchReducer.smartlists,
  );

  const libraries: Record<string, unknown> = {
    video: contents,
    casting: castingsData,
    smartlist: smartlists,
    display: displaysData,
  };

  // Seed state: still the raw, unresolved `items` (e.g. OfferSelectBox's items
  // have no uuid yet) until getItems() below replaces it with resolved items.
  const [selectedItems, setSelectedItems] = useState<SelectBoxDisplayItem[]>([
    ...items,
  ] as SelectBoxDisplayItem[]);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedSearch, setSelectedSearch] = useState("");

  const isLoading =
    (hasMediaList && !contents.isFetched) ||
    (hasSmartlistList && !smartlists.isFetched) ||
    (hasDisplayList && !isFetchedDisplays) ||
    (hasCastingList && !isFetchedCastings);

  const getSelectedItems = (
    item: SelectBoxSeedItem,
    type: string,
  ): SelectBoxDisplayItem | undefined => {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- ?? would keep an empty content_uuid instead of falling back to item.uuid
    const uuid = item?.content_uuid || item?.uuid;
    const content = getElementByUuid(uuid, libraries[type]);

    if (content) {
      // getElementByUuid is untyped (legacy JS): content's shape isn't known
      // here, it varies with `type` (media/casting/smartlist/display item).
      return {
        ...content,
        itemType: item?.content_type,
        status: ItemStatus.getStatusByType(content, type),
        isDraggable: true,
      } as SelectBoxDisplayItem;
    }

    return undefined;
  };

  const getItems = (): void => {
    const listItems: SelectBoxDisplayItem[] = [];

    items?.forEach((item) => {
      let listItem: SelectBoxDisplayItem | undefined;
      const itemType =
        item?.content_type === "audio" ? "video" : item?.content_type;

      if (itemType) {
        listItem = getSelectedItems(item, itemType);
      }

      if (listItem) {
        // Keep existing behavior: rank is stored directly on the selectbox item.
        listItem.rank = item.rank;
        listItems.push(listItem);
      }
    });

    const newItemList = hasRank
      ? renumberRanks(sortByRank(listItems))
      : listItems;
    setSelectedItems(newItemList);

    // Unresolved items are missing from the list: don't drop them from the parent.
    const isFullyResolved = listItems.length === items.length;
    const isNormalized = newItemList.some(
      (newItem, index) => newItem !== listItems[index],
    );

    if (isFullyResolved && isNormalized) onNormalizeRanks?.(newItemList);
  };

  useEffect(() => {
    if (!isLoading) getItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const selectedFilteredItems = useMemo((): SelectBoxDisplayItem[] => {
    const statusFilteredList = selectedItems.filter(
      (item) => selectedStatus === "all" || item.status === selectedStatus,
    );
    const searchText = selectedSearch.toLowerCase();

    return searchText
      ? statusFilteredList.filter((item) => matchesItemSearch(item, searchText))
      : statusFilteredList;
  }, [selectedItems, selectedStatus, selectedSearch]);

  const updateSelectedItems = useCallback((list: SelectBoxDisplayItem[]) => {
    setSelectedItems([...list]);
  }, []);

  return {
    selectedItems,
    updateSelectedItems,
    selectedStatus,
    setSelectedStatus,
    selectedSearch,
    setSelectedSearch,
    selectedFilteredItems,
  };
}
